<template>
  <section class="utpl-stats" :class="{ 'utpl-stats--no-padding': paddingStats === 'none' }"
     v-if="!loading && stats">
    <div class="utpl-stats__header">
      <h2 class="utpl-stats__title">Investigación UTPL en números</h2>
      <p class="utpl-stats__subtitle">
        UTPL, un entorno dinámico e inspirador para <strong>investigar</strong>, <strong>innovar</strong> y
        <strong>emprender</strong>
      </p>
    </div>

    <div class="container">
      <div class="row justify-content-center g-3">
        <!-- Iterar sobre cada card -->
        <div 
          v-for="card in cards" 
          :key="card.broader"
          class="col-lg-2 col-md-3 col-sm-4 col-6"
        >
          <!-- Card con enlace -->
          <a 
            v-if="getCardLink(card.broader)" 
            :href="getCardLink(card.broader)"
            class="stat-card stat-card--link"
          >
            <img 
              class="stat-card__icon"
              :src="`https://componentes_srv.utpl.edu.ec/investigacion/svg/${card.icon}`"
              :alt="card.label" 
              width="48" 
              height="48"
            />
            <h3 class="stat-card__label">{{ card.label }}</h3>
            <p class="stat-card__value">{{ formatNumber(getTotal(card.broader)) }}</p>
          </a>

          <!-- Card sin enlace (solo informativo) -->
          <div v-else class="stat-card">
            <img 
              class="stat-card__icon"
              :src="`https://componentes_srv.utpl.edu.ec/investigacion/svg/${card.icon}`"
              :alt="card.label" 
              width="48" 
              height="48"
            />
            <h3 class="stat-card__label">{{ card.label }}</h3>
            <p class="stat-card__value">{{ formatNumber(getTotal(card.broader)) }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div v-else-if="loading" class="utpl-stats__loading">
    <div class="spinner"></div>
    <p>Cargando estadísticas...</p>
  </div>

  <div v-else-if="error" class="utpl-stats__error">
    <p>{{ error }}</p>
  </div>
</template>

<script>
export default {
  name: 'UtplStatsWidget',
  props: {
    apiUrl: {
      type: String,
      // La barra final es obligatoria: sin ella el servidor responde 301 y el navegador corta la petición CORS
      default: process.env.VUE_APP_SIAC_API_URL || 'https://develop.utpl.edu.ec/siac/api/resources-summary/'
    },
    apiKey: {
      type: String,
      default: process.env.VUE_APP_SIAC_API_KEY || ''
    },
    dominio: {
      type: String,
      default: 'sica', // 'data', 'inves', o 'sica'
      validator: (value) => ['data', 'inves', 'sica'].includes(value)
    },
    paddingStats: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      loading: false,
      error: '',
      stats: null,
      // Configuración de rutas por dominio y broader
      routesConfig: {
        docentes: {
          data: '/search?search=&broader=docentes&ordering=-year&page=1',
          inves: '/perfiles',
          sica: null
        },
        articulos: {
          data: '/search?search=&broader=articulos&ordering=-year&page=1',
          inves: '/articulos',
          sica: null
        },
        libros: {
          data: '/search?search=&broader=libros&ordering=-year&page=1',
          inves: '/libros',
          sica: null
        },
        proyectos: {
          data: '/search?search=&broader=proyectos&ordering=-year&page=1',
          inves: '/proyectos',
          sica: null
        },
        grupos: {
          data: '/search?search=&broader=grupos&ordering=-year&page=1',
          inves: '/grupos',
          sica: null
        },
        propiedad_intelectual: {
          data: '/search?search=&broader=propiedad_intelectual&ordering=-year&page=1',
          inves: '/propiedad-intelectual',
          sica: null
        },
        proyecto_singular: {
          data: '/search?search=&broader=proyecto_singular&ordering=-year&page=1',
          inves: '/proyectos-singulares',
          sica: null
        },
        tesis: {
          data: '/search?search=&broader=tesis&ordering=-year&page=1',
          inves: '/tesis',
          sica: null
        },
        facultad: {
          data: '/search?search=&broader=facultad&ordering=-year&page=1',
          inves: '/facultades',
          sica: null
        },
        departamento: {
          data: '/search?search=&broader=departamento&ordering=-year&page=1',
          inves: '/departamentos',
          sica: null
        },
        laboratorio: {
          data: '/search?search=&broader=laboratorio&ordering=-year&page=1',
          inves: '/laboratorios',
          sica: null
        },
        area_conocimiento: {
          data: '/search?search=&broader=area_conocimiento&ordering=-year&page=1',
          inves: '/areas-conocimiento',
          sica: null
        },
        dominio_academico: {
          data: '/search?search=&broader=dominio_academico&ordering=-year&page=1',
          inves: '/dominios-academicos',
          sica: null
        },
        linea_investigacion: {
          data: '/search?search=&broader=linea_investigacion&ordering=-year&page=1',
          inves: '/lineas-investigacion',
          sica: null
        }
      },
      cards: [
        { broader: 'docentes', label: 'Perfiles', icon: 'docentes.svg' },
        { broader: 'articulos', label: 'Artículos', icon: 'articulos.svg' },
        { broader: 'libros', label: 'Libros', icon: 'libros.svg' },
        { broader: 'proyectos', label: 'Proyectos', icon: 'proyectos.svg' },
        { broader: 'grupos', label: 'Grupos', icon: 'grupos.svg' },
        { broader: 'propiedad_intelectual', label: 'Propiedad intelectual', icon: 'propiedad_intelectual.svg' },
        { broader: 'proyecto_singular', label: 'Proyecto singular', icon: 'proyecto_singular.svg' },
        { broader: 'tesis', label: 'Tesis', icon: 'tesis.svg' },
        { broader: 'facultad', label: 'Facultad', icon: 'facultad.svg' },
        { broader: 'departamento', label: 'Departamento', icon: 'departamento.svg' },
        { broader: 'laboratorio', label: 'Laboratorio', icon: 'laboratorio.svg' },
        { broader: 'area_conocimiento', label: 'Área de conocimiento', icon: 'area_conocimiento.svg' },
        { broader: 'dominio_academico', label: 'Dominio académico', icon: 'dominio_academico.svg' },
        { broader: 'linea_investigacion', label: 'Línea investigación', icon: 'linea_investigacion.svg' }
      ]
    }
  },
  mounted() {
    this.fetchStats()
  },
  methods: {
    async fetchStats() {
      this.loading = true
      this.error = ''
      try {
        const headers = this.apiKey ? { 'X-API-Key': this.apiKey } : {}
        const response = await fetch(this.apiUrl, { headers })
        if (!response.ok) throw new Error(`Error al cargar las estadísticas (${response.status})`)
        const data = await response.json()
        if (!Array.isArray(data.results)) throw new Error('Respuesta inesperada del servicio de estadísticas')
        this.stats = data.results
      } catch (e) {
        this.error = e.message || 'Error al cargar las estadísticas'
      } finally {
        this.loading = false
      }
    },
    getTotal(broader) {
      if (!this.stats) return 0
      const item = this.stats.find(s => s.broader === broader)
      return item ? item.total : 0
    },
    formatNumber(num) {
      return num.toLocaleString('es-ES')
    },
    getCardLink(broader) {
      const config = this.routesConfig[broader]
      if (!config || !config[this.dominio]) return null
      return config[this.dominio]
    }
  }
}
</script>

<style scoped>
@import "../../styles/vinculacion/bootstrap.css";

.utpl-stats {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.utpl-stats--no-padding {
  padding: 0;
}

.utpl-stats__header {
  text-align: center;
  margin-bottom: 50px;
}

.utpl-stats__title {
  font-size: 2rem;
  font-weight: 700;
  color: #004270;
  margin: 0 0 16px;
}

.utpl-stats__subtitle {
  font-size: 1.1rem;
  color: #636e72;
  margin: 0;
  line-height: 1.6;
}

.utpl-stats__subtitle strong {
  color: #004270;
  font-weight: 600;
}

.stat-card {
  background: white;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 250px;
  margin: 0 auto;
  display: block;
}

.stat-card--link {
  cursor: pointer;
  text-decoration: none;
  color: inherit;
}

.stat-card--link:hover {
  background: #004270;
  border-color: #004270;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 66, 112, 0.2);
}

.stat-card__icon {
  width: 48px;
  height: 48px;
  margin-bottom: 16px;
  filter: brightness(0) saturate(100%) invert(14%) sepia(87%) saturate(1450%) hue-rotate(184deg) brightness(91%) contrast(105%);
  transition: filter 0.4s ease, transform 0.3s ease;
}

.stat-card--link:hover .stat-card__icon {
  animation: colorSwap 0.6s forwards;
}

@keyframes colorSwap {
  0% {
    filter: brightness(0) saturate(100%) invert(48%) sepia(78%) saturate(408%) hue-rotate(86deg) brightness(93%) contrast(88%);
  }
  100% {
    filter: brightness(0) invert(1);
  }
}

.stat-card__label {
  font-size: 0.95rem;
  color: #004270;
  margin: 0 0 8px;
  font-weight: 500;
  transition: color 0.3s ease;
}

.stat-card--link:hover .stat-card__label {
  color: white;
}

.stat-card__value {
  font-size: 16px;
  font-weight: 700;
  color: #004270;
  margin: 0;
  line-height: 1;
  transition: color 0.3s ease;
}

.stat-card--link:hover .stat-card__value {
  color: white;
}

.utpl-stats__loading,
.utpl-stats__error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #004270;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.utpl-stats__error {
  color: #e74c3c;
}

@media (max-width: 768px) {
  .stat-card {
    padding: 24px 16px;
  }

  .stat-card__value {
    font-size: 2rem;
  }

  .utpl-stats__title {
    font-size: 1.5rem;
  }

  .utpl-stats__subtitle {
    font-size: 1rem;
  }
}
</style>